import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchCustomerOrders, type CustomerOrder } from '../customerOrdersApi'
import { asOrderErrorMessage } from '../helpers/errors'
import { useCustomerProtectedList } from './useCustomerProtectedList'

export const ORDER_LIST_LIMIT = 30

const CHECKOUT_SYNC_TIMEOUT_MS = 90_000
const CHECKOUT_SYNC_INTERVAL_MS = 2_500

type UseOrdersListWithCheckoutSyncInput = {
  authenticated: boolean
  token: string | null
  loadOrdersList: boolean
  enableCheckoutConsistencyPolling: boolean
}

type UseOrdersListWithCheckoutSyncResult = {
  orders: CustomerOrder[]
  ordersLoading: boolean
  ordersError: string
  retryOrders: () => void
  checkoutSyncActive: boolean
}

export const useOrdersListWithCheckoutSync = ({
  authenticated,
  token,
  loadOrdersList,
  enableCheckoutConsistencyPolling,
}: UseOrdersListWithCheckoutSyncInput): UseOrdersListWithCheckoutSyncResult => {
  const fetchOrders = useCallback(
    (sessionToken: string | null) => fetchCustomerOrders(sessionToken, { limit: ORDER_LIST_LIMIT }),
    [],
  )

  const {
    items: orders,
    setItems: setOrders,
    loading: ordersLoading,
    error: ordersError,
    retry: retryOrders,
  } = useCustomerProtectedList<CustomerOrder>({
    enabled: authenticated && loadOrdersList,
    token,
    fetchItems: fetchOrders,
    toErrorMessage: asOrderErrorMessage,
    requireToken: false,
  })

  const [checkoutSyncActive, setCheckoutSyncActive] = useState(false)
  const checkoutSyncCompletedRef = useRef(false)
  const checkoutSyncRunningRef = useRef(false)

  useEffect(() => {
    if (!enableCheckoutConsistencyPolling) {
      checkoutSyncCompletedRef.current = false
      checkoutSyncRunningRef.current = false
      setCheckoutSyncActive(false)
      return
    }

    if (!authenticated || !loadOrdersList || ordersLoading) {
      return
    }

    if (checkoutSyncCompletedRef.current || checkoutSyncRunningRef.current) {
      return
    }

    const latestOrder = orders[0]
    const shouldStartPolling =
      !latestOrder ||
      latestOrder.status === 'created' ||
      latestOrder.paymentState === 'pending' ||
      latestOrder.paymentState === 'unpaid'

    if (!shouldStartPolling) {
      checkoutSyncCompletedRef.current = true
      setCheckoutSyncActive(false)
      return
    }

    checkoutSyncRunningRef.current = true
    setCheckoutSyncActive(true)
    let active = true
    let timer: ReturnType<typeof setTimeout> | null = null
    const startedAtMs = Date.now()

    const stopPolling = () => {
      checkoutSyncCompletedRef.current = true
      checkoutSyncRunningRef.current = false
      if (active) {
        setCheckoutSyncActive(false)
      }
      if (timer) {
        clearTimeout(timer)
      }
    }

    const scheduleNextTick = () => {
      timer = setTimeout(() => {
        void tick()
      }, CHECKOUT_SYNC_INTERVAL_MS)
    }

    const tick = async () => {
      if (!active) return

      if (Date.now() - startedAtMs >= CHECKOUT_SYNC_TIMEOUT_MS) {
        stopPolling()
        return
      }

      try {
        const nextOrders = await fetchOrders(token)
        if (!active) return
        setOrders(nextOrders)

        const nextLatestOrder = nextOrders[0]
        if (!nextLatestOrder) {
          scheduleNextTick()
          return
        }

        const isStillInitialState =
          nextLatestOrder.status === 'created' &&
          (nextLatestOrder.paymentState === 'pending' || nextLatestOrder.paymentState === 'unpaid')

        if (!isStillInitialState) {
          stopPolling()
          return
        }
      } catch {
        if (!active) return
        // Keep polling best-effort until timeout.
      }

      scheduleNextTick()
    }

    void tick()

    return () => {
      active = false
      checkoutSyncRunningRef.current = false
      if (timer) {
        clearTimeout(timer)
      }
      setCheckoutSyncActive(false)
    }
  }, [
    authenticated,
    enableCheckoutConsistencyPolling,
    fetchOrders,
    loadOrdersList,
    orders,
    ordersLoading,
    setOrders,
    token,
  ])

  return {
    orders,
    ordersLoading,
    ordersError,
    retryOrders,
    checkoutSyncActive,
  }
}
